import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Canlılık',
    description: 'Grup düzeyinde anonimleştirilmiş, gösterge niteliğinde iyi oluş eğilimleri.',
    noScope: 'Canlılık verilerini görmek için bir şirket seçin.',
    noDepartments: 'Burada görebileceğiniz istatistiklere sahip bir departman yok.',
    disclaimer:
      'Tüm veriler <strong>gösterge niteliğinde iyi oluş bilgileridir</strong> ve <strong>tıbbi teşhis niteliği taşımaz</strong>. Eğilimler yalnızca yeterli sayıda katılımcı olduğunda gösterilir (tasarım gereği gizlilik).',
  },
  filters: {
    title: 'Filtreler',
    department: {
      label: 'Departman',
    },
    location: {
      label: 'Konum',
      all: 'Tüm konumlar',
    },
  },
  badge: {
    measured: 'Ölçülen',
    aiDerived: 'Yapay zeka analizi',
  },
  level: {
    good: 'İyi',
    opportunity: 'Fırsat',
  },
  statusPill: {
    healthy: 'Sağlıklı seviye',
    opportunity: 'Yardım fırsatı',
    strongForward: 'Güçlü ve yükseliyor',
  },
  trend: {
    flat: 'Değişim yok',
    vsPrevious: 'önceki döneme göre',
    vsAverage: 'dönem ortalamasına göre',
    up: 'Yükseliyor',
    down: 'Hafif düşük',
    upBad: 'Hafif yüksek',
    week: 'hf.',
  },
  units: {
    hoursMinutes: '{{h}}s {{m}}d',
    average: 'ort. {{value}}',
  },
  status: {
    noData: 'Bu dönemde veri yok.',
  },
  table: {
    level: 'Seviye',
    trend: 'Eğilim',
    action: 'Eylem',
    actionNone: '—',
    actionLink: 'Departman',
    actionLinkTitle: 'Bu departmana git',
  },
  insight: {
    title: 'Canlılık verileri ne diyor',
    unavailable: 'Özet için henüz yeterli veri yok.',
    vitality: {
      up: 'Canlılık yükseliyor ({{period}} %{{pct}}), katılım oranı %{{participation}}.',
      down: 'Canlılık düşüyor ({{period}} %{{pct}}), katılım oranı %{{participation}}.',
      flat: 'Canlılık sabit ({{period}} %{{pct}}), katılım oranı %{{participation}}.',
      noParticipation: 'Canlılık sabit ({{period}} %{{pct}}).',
    },
    period: {
      last7Days: 'son 7 günde',
      last30Days: 'bu ay',
      thisWeek: 'bu hafta',
      thisMonth: 'bu ay',
      custom: 'bu dönemde',
    },
    stress: {
      up: 'Stres yükseliyor (%{{pct}})',
      down: 'stres azalıyor (%{{pct}})',
      flat: 'stres sabit kalıyor',
    },
    sleep: {
      longer: 've çalışanlar ortalama {{minutes}} dakika daha uzun uyuyor.',
      shorter: 've çalışanlar ortalama {{minutes}} dakika daha kısa uyuyor.',
      same: 've çalışanlar yaklaşık aynı süre uyuyor.',
    },
    mostActiveDay: 'En aktif gün {{day}}.',
  },
  cards: {
    vitalityScore: {
      title: 'Canlılık puanı',
      unit: '%',
      info: 'Uyku, aktivite, HRV, stres ve dinlenik kalp atış hızına dayalı birleşik bir puan.',
    },
    sleep: {
      title: 'Uyku',
      unit: '',
      info: 'Gece başına ortalama uyku süresi.',
    },
    stress: {
      title: 'Stres',
      unit: '%',
      info: 'Ölçülen stres seviyesi; ne kadar düşükse o kadar sakin.',
    },
    recovery: {
      title: 'Toparlanma',
      unit: '%',
      info: 'Vücudun uyku, HRV ve strese dayalı olarak ne kadar iyi toparlandığı.',
    },
    activity: {
      title: 'Aktivite',
      unit: 'adım/gün',
      info: 'Günlük ortalama adım sayısı.',
    },
    hrv: {
      title: 'HRV',
      unit: 'ms',
      info: 'Kalp atış hızı değişkenliği — toparlanma ve dayanıklılık göstergesi.',
    },
    restingHeartRate: {
      title: 'Dinlenik kalp atış hızı',
      unit: 'bpm',
      info: 'Seçilen dönemde ölçülen en düşük kalp atış hızı, dinlenik kalp atış hızının bir yaklaşımı olarak.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'Canlılık iyi bir yönde ilerliyor — mevcut rutinleri koruyun.',
      opportunity:
        'Canlılıkta gelişim alanı var — kısa bir takım görüşmesi neyin engel olduğunu ortaya çıkarabilir.',
    },
    sleep: {
      good: 'Takımın uykusu sağlıklı görünüyor — bir işlem gerekmiyor.',
      opportunity:
        'Uyku hedefin altında — yatmadan önce ekran süresini azaltma ve sabit bir akşam rutini hatırlatması düşünülebilir.',
    },
    stress: {
      good: 'Stres seviyesi kontrol altında — izlemeye devam edin.',
      opportunity:
        'Stres yüksek — iş yükü hakkında kısa bir görüşme veya toparlanmaya yönelik bir aktivite yardımcı olabilir.',
    },
    recovery: {
      good: 'Toparlanma güçlü — takım efor sonrası iyi toparlanıyor.',
      opportunity:
        'Toparlanma geride kalıyor — dinlenme günleri ile iş yükünün dengeli olup olmadığına bakın.',
    },
    activity: {
      good: 'Aktivite seviyesi sağlıklı — bir işlem gerekmiyor.',
      opportunity:
        'Aktivite hedefin altında — yürüyerek toplantı veya adım mücadelesi yardımcı olabilir.',
    },
    hrv: {
      good: 'HRV sağlıklı görünüyor — bir işlem gerekmiyor.',
      opportunity:
        'HRV beklenenden düşük — bu genellikle stres veya kötü uykuyla ilişkilidir; birlikte konuşmaya değer.',
    },
    restingHeartRate: {
      good: 'Dinlenik kalp atış hızı sağlıklı bir aralıkta.',
      opportunity:
        'Dinlenik kalp atış hızı yüksek — stres veya azalan uyku ile birlikte bu işaretlenmeye değer.',
    },
  },
};
